import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import hrApi from "./hrApi";
import SignaturePad from "./SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NameInputs } from "./NameInputs";
import { toast } from "sonner";
import { ShieldCheck, Download, FileText } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
function Shell({ children }) { return <div className="min-h-screen bg-slate-100 py-6 px-2 sm:px-4"><div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-sm p-4 sm:p-6">{children}</div></div>; }

function SignBox({ token, d, onDone }) {
  const [name, setName] = useState(d.employee_name || "");
  const [agree, setAgree] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim() || !agree) return toast.error("Type your first and last name and tick the box");
    if (!sig) return toast.error("Please draw your signature");
    setBusy(true);
    try { await hrApi.post(`/public/policy/${token}/sign`, { typed_name: name, agreed: agree, signature_data: sig }); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };
  return (
    <div className="space-y-3 rounded-2xl border border-indigo-100 bg-indigo-50/30 p-4" data-testid="policy-sign">
      <label className="flex items-start gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" data-testid="policy-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="w-4 h-4 mt-0.5 accent-indigo-500" /> I have read and understood this policy and agree to comply with it.</label>
      <NameInputs value={name} onChange={setName} testid="policy-name" className="h-11 mt-1 bg-white" />
      <div><label className="text-xs font-semibold text-slate-600">Your signature *</label><SignaturePad onChange={setSig} /></div>
      <Button data-testid="policy-submit" disabled={busy} onClick={submit} className="w-full h-12 rounded-xl bg-indigo-600">{busy ? "Signing…" : "Sign & submit"}</Button>
    </div>
  );
}

export default function PolicyPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = () => hrApi.get(`/public/policy/${token}`).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  useEffect(() => { load(); }, [token]);
  if (err) return <Shell><p data-testid="policy-error" className="text-center text-slate-600">{err}</p></Shell>;
  if (!d) return <Shell><p className="text-center text-slate-400">Loading…</p></Shell>;
  const done = d.status === "completed";
  return (
    <Shell>
      <div className="space-y-4" data-testid="policy-page">
        <div className="flex items-center gap-2 text-indigo-600 text-xs font-bold uppercase tracking-wider"><FileText size={16} /> Company policy · G &amp; L One</div>
        <h1 className="font-heading font-extrabold text-2xl text-slate-800" data-testid="policy-title">{d.policy_name}</h1>
        {done ? (
          <div className="text-center space-y-3 py-6" data-testid="policy-done">
            <div className="w-14 h-14 rounded-full bg-teal-100 grid place-items-center mx-auto"><ShieldCheck className="text-teal-600" size={28} /></div>
            <p className="text-slate-600">Thanks, your acknowledgement has been signed and filed with G &amp; L One.</p>
            <a href={`${BASE}/api/hr/public/policy/${token}/signed`} data-testid="policy-download-signed" className="inline-flex items-center gap-2 rounded-2xl border border-teal-200 bg-teal-50 px-4 h-12 text-sm font-semibold text-teal-700"><Download size={16} /> Download your signed copy (PDF)</a>
          </div>
        ) : <>
          <a href={`${BASE}/api/hr/public/policy-file/${token}`} target="_blank" rel="noreferrer" data-testid="policy-open-pdf" className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600"><Download size={15} /> Open / download the PDF</a>
          <div className="space-y-2">{Array.from({ length: d.pages }).map((_, i) => <img key={i} src={`${BASE}/api/hr/public/policy/${token}/page/${i}`} alt={`Page ${i + 1}`} loading="lazy" className="w-full rounded-lg border border-slate-200" data-testid={`policy-page-${i}`} />)}</div>
          <SignBox token={token} d={d} onDone={load} />
        </>}
      </div>
    </Shell>
  );
}
